// src/contexts/RecordContext.tsx
"use client";

import { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  ReactNode,
  useMemo,      // ← ここで一緒に
  useRef,
  useCallback        // ← 追加
} from "react";

import { UserProfile } from "@/types/UserProfile";

// /api/user-courses の返事：コースID → そのコースの権利ドキュメント（tier・source など。無いコースは含まれない）
type UserCourses = Record<string, { tier?: string; source?: string; [k: string]: unknown } | undefined>;
import { useAuthContext } from "@/contexts/AuthContext";
import { useRouter } from "next/navigation";
import type { RecordEntry, DraftEntry } from "@/types/Entry";
import type { CardType } from "@/types/Plan";
import type { CardData } from "@/types/CardData";
import { createLogger } from "@/lib/logger";
import { useEventLogger } from "@/lib/useEventLogger";
import { getEffectivePlan, computeCardCapabilities } from "@/lib/planMap";

// UI 専用：Sidebar / HowTo / 権限制御で使う最小プロファイル
type UIUserProfile = {
  user_plan?: string | null;
  app?: any;
  nazeka_member?: boolean;
};

const dbg = createLogger("record");
// 既存の log(...) 呼び出しをそのまま生かすためのエイリアス
const log = (...args: any[]) => dbg.debug(...args);

export interface Session {
  id?: string;             // FirestoreドキュメントID
  sessionId?: string;      // セッション固有ID
  topicId?: string | null;
  topic: string;
  cardType: string;
  date: string;
  entries: RecordEntry[];
  answerToTopic?: string;
  interactionType?: string;
  isDraft?: boolean;
  uid?: string;
}

interface Topic {
  id?: string;
  topic: string;
  interactionType?: string;
  planAccess?: string[];
  // 2026-09-08・コースのレッスン別お題スコープ対応（TASKS_ACTIVE.md 9/5〜9/8エントリ
  // 「Kazutoの実地作業で判明した3つの技術的ブロッカー」派生対応）。selectedTopicは
  // アプリ全体で1つの共有状態のため、この値を「どのコースレッスン向けに選ばれたか」を
  // 示す任意タグとして使う。トップ画面・ルーム・サイドバー等、この値を意識しない既存の
  // 呼び出し元は一切影響を受けない（未設定のままなら従来通りの挙動）。
  lessonKey?: string;
}

export type SelectedHowToStep = {
  key: string;
  text: string;
  action?: "draw3";
  autoRun?: boolean;
  ctaLabel?: string;
  // 2026-09-08・上記TopicのlessonKeyと同じ考え方（TASKS_ACTIVE.md参照）。この使い方ステップが
  // どのコースレッスン向けに反映されたかの任意タグ。未設定なら従来通りどこでも有効
  // （サイドバーの使い方ガイド等、既存の呼び出し元は無変更・無影響）。
  lessonKey?: string;
};

export interface RecordContextType {
  log: (event: string, props?: Record<string, any>) => void;
  recordMode: boolean;
  setRecordMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCardType: CardType;
  setSelectedCardType: (type: CardType) => void;
  // 2026-10-14・3枚語りの無料開放（TASKS_ACTIVE.md 10/14エントリ）用。画面上の選択だけを切り替え、
  // /api/user-profileへの保存（card_types_allowedへの追記を伴う）もlocalStorageへの保存もしない。
  setSelectedCardTypeLocal: (type: CardType) => void;
  // 2026-08-29・案b実装（TASKS_ACTIVE.md 8/29エントリ「Kazuto決定（案b）」参照）：
  // course_standard等、コース購入entitlementが特定のカードタイプへのアクセスを
  // 追加で許可する場合に使う。computeCardCapabilities（editionsベース）の結果に
  // 加算するための軽量な仕組みで、Firestore側のentitlements.editionsは変更しない
  // （実購入エンタイトルメント連携ができた際は、そちらへの正式な統合を検討する）。
  extraAllowedCardTypes: CardType[];
  setExtraAllowedCardTypes: React.Dispatch<React.SetStateAction<CardType[]>>;
  inputEntries: RecordEntry[];
  setInputEntries: React.Dispatch<React.SetStateAction<RecordEntry[]>>;
  recordedEntries: Session[];
  setRecordedEntries: React.Dispatch<React.SetStateAction<Session[]>>;
  selectedCards: string[];
  setSelectedCards: React.Dispatch<React.SetStateAction<string[]>>;
  selectedHowToStep: SelectedHowToStep | null;
  setSelectedHowToStep: (step: SelectedHowToStep | null) => void;
  selectedTopic: Topic | null;
  setSelectedTopic: (t: Topic | null) => void;
  recordSidebarOpen: boolean;
  setRecordSidebarOpen: (open: boolean) => void;
  activeSidebarSection: string;
  setActiveSidebarSection: (section: string) => void;
  activeTopicsTab: "list" | "register";
  setActiveTopicsTab: (tab: "list" | "register") => void;
  planRevision: number;
  effective_plan: string | null;
  newTopic: {
    topic: string;
    topicType: string;
  };
  setNewTopic: (topic: { topic: string; topicType: string }) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  openSection: string | null;
  setOpenSection: (section: string | null) => void;
  draftEntries: DraftEntry[];
  setDraftEntries: React.Dispatch<React.SetStateAction<DraftEntry[]>>;
  currentSessionId: string | null;
  setCurrentSessionId: (id: string | null) => void;
  fixCurrentSessionId: (id: string) => void;
  userProfile: UserProfile | null;
  setUserProfile: (profile: UserProfile | null) => void;
  userProfileLoading: boolean;
  setUserProfileLoading: (loading: boolean) => void;
  answerToTopic?: string;
  setAnswerToTopic: (answer: string) => void;
  recentSession: Session | null;
  setRecentSession: (s: Session | null) => void;
  cards: CardData[];
  setCards: React.Dispatch<React.SetStateAction<CardData[]>>;
  fetchUserProfile: () => Promise<void>;
  // 2026-11-02：/api/user-courses の共有（起動時に1回取得）。status が "loading" の間は未確認、"error" は取得失敗。
  userCourses: UserCourses | null;
  userCoursesStatus: "loading" | "ok" | "error";
  refreshUserCourses: () => Promise<void>;
  suppressAutoRestoreOnceRef: React.MutableRefObject<boolean>;
  clearInputEntries: () => void; // ← 追加：保存後に明示クリアしたいとき用
  commitSavedSession: (sessionId: string, saved?: Partial<Session>) => void; // ← 追加：保存直後に現在セッションを固定
  skipAutoRestoreNextLoad: () => void;
  skipNextMirror: () => void;
  optimisticArchive: (sessionId: string) => void;
  sessionMode: "card" | "text";
  setSessionMode: (mode: "card" | "text") => void;
}

interface RecordProviderProps {
  children: ReactNode;
}

const RecordContext = createContext<RecordContextType | undefined>(undefined);

export const RecordProvider = ({ children }: RecordProviderProps) => {
  const { log: logEvent } = useEventLogger();
  const logTopicEvent = logEvent;
  // ※ 画面・各コンポーネントに渡す selectedCardType は、userProfile の宣言の後で導出する（下）。
  const [selectedCardTypeRaw, setSelectedCardType] = useState<CardType>("free");
  // 2026-08-29・案b実装：コース購入entitlementによる追加カードタイプ許可（上記interface参照）。
  const [extraAllowedCardTypesState, setExtraAllowedCardTypes] = useState<CardType[]>([]);
  // SSR hydration完了後にlocalStorageから復元（lazy initializerはSSRで無視されるためuseEffectで対応）
  useEffect(() => {
    try {
      const saved = localStorage.getItem("kataruta.card_type");
      if (saved) setSelectedCardType(saved as CardType);
    } catch {}
  }, []);
  const handleSetSelectedCardType = (type: CardType) => {
    try {
      if (type !== selectedCardType) {
        localStorage.removeItem("kataruta.play_cards"); // タイプが変わる時だけクリア
      }
      localStorage.setItem("kataruta.card_type", type);
    } catch {}
    // 2026-08-18・CEO依頼で削除：ここでの`card_draw`発火は、デッキ切替時に
    // SidebarCardType.tsx（handleSelectCardType）が既に発火している`card_type_change`
    // と完全に重複していた（デッキ切替1回につき別名イベントが2つ同時発火）。
    // `card_draw`という名前は「実際にカードがめくられた瞬間」用に予約し直し、
    // 真の発火箇所をPlayArea.tsx（handleAddCard）に新設した（TASKS_ACTIVE.md 8/18エントリ参照）。

    // 🔒 新規ユーザー・未初期化状態では保存しない
    if (
      !userProfile ||
      !Array.isArray(userProfile.card_types_allowed) ||
      userProfile.card_types_allowed.length === 0 ||
      userProfile.user_plan === "pending"
    ) {
      // UI状態だけ更新して終了（APIは叩かない）
      setSelectedCardType(type);
      return;
    }

    setSelectedCardType(type);
    setUserProfile(prev => prev ? { ...prev, card_type_selected: type } : prev);
  
    // ★ 正しい保存 API（/api/user-profile）でカードタイプを更新
    fetch("/api/user-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        // 🔥 必須：uid を常に送る（route.ts の merged ロジックが正しく動く）
        uid: clerkUser?.id,
        card_type_selected: type,
        last_switched_at: new Date().toISOString(),
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          // 2026-10-14・画面を止めるalert()をやめ、ログのみにする。この保存はコース画面を開いた
          // 直後などに自動でも走る背景同期で、画面上の選択（上のsetSelectedCardType）は既に
          // 反映済み。通信の一時失敗でモーダルが連続表示され、操作を妨げていた（Kazuto実機報告）。
          console.warn("[card_type] save failed:", err?.error ?? res.status);
          return;
        }
        // 保存成功 → 最新プロフィールを取得
        fetchUserProfile();
      })
      .catch((e) => {
        console.warn("[card_type] save network error:", e);
      });
  };
  
  const [selectedCards, setSelectedCards] = useState<string[]>([]);

  const [selectedHowToStep, setSelectedHowToStep] = useState<SelectedHowToStep | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [inputEntries, setInputEntries] = useState<RecordEntry[]>([]);

  const [recordedEntries, setRecordedEntries] = useState<Session[]>([]);
  const [recordSidebarOpen, setRecordSidebarOpen] = useState(true);
  const [activeSidebarSection, setActiveSidebarSection] = useState("sessionLog");
  const [activeTopicsTab, setActiveTopicsTab] = useState<"list" | "register">("list");
  const [newTopic, setNewTopic] = useState({ topic: "", topicType: "" });
  const [recordMode, setRecordMode] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [openSection, setOpenSection] = useState<string | null>(null);

  // テキスト式モード（localStorage: kataruta.session_mode）
  const [sessionMode, setSessionModeState] = useState<"card" | "text">("card");
  useEffect(() => {
    try {
      const saved = localStorage.getItem("kataruta.session_mode");
      if (saved === "card" || saved === "text") setSessionModeState(saved);
    } catch {}
  }, []);
  const setSessionMode = (mode: "card" | "text") => {
    setSessionModeState(mode);
    try { localStorage.setItem("kataruta.session_mode", mode); } catch {}
  };
  const [draftEntries, setDraftEntries] = useState<DraftEntry[]>([]);
  const [currentSessionId, _setCurrentSessionId] = useState<string | null>(null);

  const setCurrentSessionId = useCallback((next: any) => {
    if (typeof next === "string" || next == null) {
      _setCurrentSessionId(next ?? null);
      return;
    }
    if (typeof next === "object") {
      const extracted =
        (next as any)?.sessionId ?? (next as any)?.id ?? null;
      // 開発では目立つ警告、本番では軽量ロガーへ（PII/騒音を避けつつ検知性は維持）
     if (process.env.NODE_ENV !== "production") {
       console.warn(
         "[RecordCtx] setCurrentSessionId にオブジェクトが渡されました。抽出ID:",
         extracted,
         { type: typeof next }
       );
     }
     dbg.warn("setCurrentSessionId: object passed", {
       extracted,
       nextType: typeof next
     });
      console.trace();
      _setCurrentSessionId(typeof extracted === "string" ? extracted : null);
      return;
    }
    dbg.warn("setCurrentSessionId: invalid type", { type: typeof next, value: next });
    _setCurrentSessionId(null);
  }, []);

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [planRevision, setPlanRevision] = useState(0);
  const [userProfileLoading, setUserProfileLoading] = useState<boolean>(true);

  // 2026-11-02・購入者のスタンダード（案B）：/api/user-profile の entitled_card_types（course_standard の
  // 購入権利。サーバーが users/{uid}/courses/course_standard の tier を見て返す）を、20選のページを
  // 開いたときに足す分（state）と合わせて、extraAllowedCardTypes として渡す。プロフィールと同じ描画で
  // 揃うので、PlayArea・サイドバーは最初から standard を許可済みとして扱う。
  const profileEntitled = useMemo<CardType[]>(
    () =>
      Array.isArray(userProfile?.entitled_card_types)
        ? (userProfile!.entitled_card_types as CardType[])
        : [],
    [userProfile]
  );
  const extraAllowedCardTypes = useMemo<CardType[]>(
    () => Array.from(new Set([...extraAllowedCardTypesState, ...profileEntitled])) as CardType[],
    [extraAllowedCardTypesState, profileEntitled]
  );
  // 案B（自動固定）：購入権利があり、いまの許可が「free」だけの人（無料プラン・版の購入なし）は、
  // 選択を standard に固定する。有料プラン・版(editions)を持つ人は対象外（従来どおり）。
  // 保存はしない（/api/user-profile へ POST しない。表示だけの導出）。
  const entitledFixedType = useMemo<CardType | null>(() => {
    if (!userProfile || !profileEntitled.includes("standard" as CardType)) return null;
    const caps = computeCardCapabilities(userProfile);
    return caps.allowed.length === 1 && caps.allowed[0] === "free" ? ("standard" as CardType) : null;
  }, [userProfile, profileEntitled]);
  const selectedCardType: CardType = entitledFixedType ?? selectedCardTypeRaw;
  const [answerToTopic, setAnswerToTopic] = useState<string>("");
  const [recentSession, setRecentSession] = useState<Session | null>(null);
  const [cards, setCards] = useState<CardData[]>([]); 

  const router = useRouter();

  const hydratedCurrentRef = useRef(false);
  const didRedirectRef = useRef(false);
  const STORAGE_KEY = "kataruta.currentSessionId";
  const SKIP_SENTINEL = "__NONE__";

  // 🧪 追加: デバッグ&ガード系
  const dbgSeq = useRef(0);

  // 「直近で保存したセッション」を覚えておく（空上書き抑止に使う）
  const lastSavedRef = useRef<{ id: string; at: number } | null>(null);

  // hydrate の同時実行制御（古い実行を捨てる）
  const hydrateRunId = useRef(0);

  // 保存後などで手動クリアしたいとき用のヘルパ
  const clearInputEntries = () => setInputEntries([]);

  const skipAutoRestoreNextLoad = () => {
    if (typeof window === "undefined") return;
    try { localStorage.setItem(STORAGE_KEY, SKIP_SENTINEL); } catch {}
  };

  const suppressAutoRestoreOnceRef = useRef(false);

  // ▼ 追加：recordedEntries を外部から一度だけ上書きしないためのフラグ
  const skipNextMirrorRef = useRef(false);
  const skipNextMirror = () => { skipNextMirrorRef.current = true; };

  const fetchUserProfile = async () => {
    if (!clerkUser || !clerkUser.id) return;

    // 2026-07-19追加：既に同じユーザーのuserProfileを取得済みかどうかを先に控えておく。
    // バックグラウンドの再取得（authLoadingの一時変化などで再実行される）が一時的な
    // 瞬断で失敗しただけの場合、既に取得済みのプロフィールをnullに戻さない。
    // PlayArea.tsx側は「userProfileLoading中は早期リターンしてフックを呼ばない」実装
    // のため、既にマウント済みの画面でuserProfileがnullに戻るとフック呼び出し数が
    // 前回レンダリングと食い違い、Reactがクラッシュする（React error #310）。
    // 7/11修正でuserProfileLoadingの再トリガーは塞いだが、userProfile自体がnullに
    // 戻る経路（このfetchUserProfile内のエラー処理）は未対応だった。ログイン直後・
    // Clerkのセッションがサーバー側にまだ完全同期していない一瞬の隙間で再現した。
    const hadResolvedProfile = userProfile?.uid === clerkUser.id;

    try {
      // NOTE:
      // /api/user-profile は auth() の userId を使うので、query userId は不要。
      // protect-rewrite/404 HTML 混入の回避にも効く。
      const res = await fetch("/api/user-profile", {
        cache: "no-store",
      });

      if (!res.ok) {
        console.warn("fetchUserProfile: non-OK", { status: res.status });
        if (!hadResolvedProfile) {
          setUserProfile(null);
          router.replace("/account");
        }
        return;
      }

      // 🔒 追加：HTMLガード（Clerk protect-rewrite対策）
      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        console.warn("fetchUserProfile: non-JSON response", { contentType });
        if (!hadResolvedProfile) {
          setUserProfile(null);
          router.replace("/account");
        }
        return;
      }

      let data = await res.json();

      // ====== ここから既存のフィールド補正（そのまま維持） ======
      if (data && typeof data === "object") {
        if (!data.user_plan) data.user_plan = "pending";

        if (!data.card_types_allowed) data.card_types_allowed = [];

        if (
          !data.card_type_selected &&
          Array.isArray(data.card_types_allowed) &&
          data.card_types_allowed.length > 0
        ) {
          data.card_type_selected = data.card_types_allowed[0];
        }
      }
      // ====== ここまで ======

      if (data && !data.error) {
        setUserProfile(data);
      } else if (!hadResolvedProfile) {
        setUserProfile(null);
      }

    } catch (error) {
      console.error("fetchUserProfile error:", error);
      if (!hadResolvedProfile) {
        setUserProfile(null);
        router.replace("/account");
      }
    } finally {
      setUserProfileLoading(false);
    }
  };

  // FirestoreのusersコレクションからuserProfileを取得
  const { clerkUser, isLoading: authLoading } = useAuthContext();

  const initSelectedTypeOnceRef = useRef(false);

  useEffect(() => {
    // 👇 ここが挿入ポイント
    if (authLoading || !clerkUser?.id) {
      // 2026-07-11修正：既にuserProfileを取得済みの場合は、タブ復帰時などの
      // Clerkバックグラウンド再検証でauthLoadingが一時的にtrueへ戻っても
      // userProfileLoadingは戻さない。ここでtrueに戻すと、PlayArea.tsx側の
      // 「userProfileLoading中は早期リターンしてフックを呼ばない」実装により、
      // 既にマウント済みの画面でフック呼び出し数が前回レンダリングと食い違い、
      // Reactがクラッシュする（React error #310）。取得済みプロフィールは
      // このガードにより保持され続ける。
      if (!userProfile) {
        setUserProfileLoading(true);
      }
      return;
    }
    // サインインしていない場合
    if (!clerkUser.id) {
      setUserProfileLoading(false);
      setUserProfile(null);
      return;
    }
    // すでに同じidのuserProfileが存在すればfetchしない
    if (userProfile?.uid === clerkUser.id) {
      setUserProfileLoading(false);
      return;
    }
    setUserProfileLoading(true);
  
    fetchUserProfile();
  }, [clerkUser?.id, authLoading]);

  // 2026-11-02・CEO指示（user-courses の共有・方法A）：購入済みコース（users/{uid}/courses）を、起動時に
  // 1回だけ取得して共有する。以前は、トップ・サイドバー・トップバー・アカウントの帯が、それぞれ同じ通信を
  // 重ねて取っていた。取得に失敗しても一覧が空になりっぱなしにならないよう、最大3回（0・1.5・4.5秒）やり直す。
  // 購入の直後などで最新が要るときは refreshUserCourses を呼ぶ。
  const [userCourses, setUserCourses] = useState<UserCourses | null>(null);
  const [userCoursesStatus, setUserCoursesStatus] = useState<"loading" | "ok" | "error">("loading");
  const refreshUserCourses = useCallback(async () => {
    for (let i = 0; i < 3; i++) {
      try {
        const r = await fetch("/api/user-courses", { cache: "no-store" });
        if (r.ok) {
          setUserCourses(await r.json());
          setUserCoursesStatus("ok");
          return;
        }
      } catch {
        // 次のやり直しへ
      }
      if (i < 2) await new Promise((res) => setTimeout(res, i === 0 ? 1500 : 3000));
    }
    setUserCoursesStatus("error");
  }, []);
  useEffect(() => {
    if (authLoading) return;
    if (!clerkUser?.id) {
      // ログアウト・別ユーザー：前の人のコースを残さない
      setUserCourses(null);
      setUserCoursesStatus("loading");
      return;
    }
    void refreshUserCourses();
  }, [clerkUser?.id, authLoading, refreshUserCourses]);

  useEffect(() => {
    if (!userProfile || initSelectedTypeOnceRef.current) return;

    // 🔁 プランが確定したら planRevision を進める
    if (userProfile.user_plan) {
      setPlanRevision(prev => prev + 1);
    }

    // localStorageに保存済みの値を優先する（同一デバイスでのリロード）
    // localStorageがなければFirestoreの値を使う（初回ログインや別デバイスへの同期）
    try {
      const fromLocalStorage = localStorage.getItem("kataruta.card_type");
      if (!fromLocalStorage) {
        const fromProfile = userProfile.card_type_selected;
        if (fromProfile && fromProfile !== selectedCardType) {
          setSelectedCardType(fromProfile);
        }
      }
      // localStorageに値がある場合はそちらを優先（上書きしない）
    } catch {
      // localStorage利用不可時はFirestoreの値を使う
      const fromProfile = userProfile.card_type_selected;
      if (fromProfile && fromProfile !== selectedCardType) {
        setSelectedCardType(fromProfile);
      }
    }

    initSelectedTypeOnceRef.current = true; // ← "初期一回だけ"にする
    // selectedCardType を依存に入れない：再初期化を防ぐ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  // 🌱 自動保存（相談役決定 2026-07-08・IDEAS.md「自動保存＝③捕捉 実装決定」）
  // カード式のみ・draftEntriesが変化する（＝カードを引く/編集する）たびdebounce付きで
  // /api/session-log へupsert保存する。「保存する」ボタンの動作・意味は変更しない（①は後回し）。
  // 常にisDraft:trueの私的ドラフトとして書く（完走フラグではない・相談役裁定「状態は2つだけ」）。
  // 既存の明示保存フローとは意図的に完全独立（sessionIdを共有しない）。
  // 重複ドキュメントが生じる可能性はあるが、相談役承認済み（「重複整理は着手ついでで可・急がない」）。
  const autoSaveSessionIdRef = useRef<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (sessionMode !== "card") return; // テキスト式は後回し（相談役裁定）
    if (!clerkUser?.id) return;

    if (draftEntries.length === 0) {
      // 下書きが空＝保存済み/キャンセル済み。次の下書きのためにIDをリセット
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      autoSaveSessionIdRef.current = null;
      return;
    }

    // 🔧 このドラフトでまだ一度も自動保存していない（＝初回捕捉）場合は即時保存。
    // 「書く→即・保存する」のように素早く操作されると、2秒debounceの予約が
    // draftEntriesクリアによってキャンセルされ、一度も自動保存されないまま終わる
    // 事象が実機テストで確認されたため（2026-07-08）。2回目以降の編集のみdebounceする。
    const delay = autoSaveSessionIdRef.current ? 2000 : 300;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch("/api/session-log", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: clerkUser.id,
            sessionId: autoSaveSessionIdRef.current ?? undefined,
            entries: draftEntries,
            date: new Date().toISOString(),
            cardType: selectedCardType,
            topicId: selectedTopic?.id ?? null,
            topic: selectedTopic?.topic ?? "",
            answerToTopic: answerToTopic ?? "",
            interactionType: selectedTopic?.interactionType || "simple",
            isDraft: true,
          }),
        });
        if (res.ok) {
          const resData = await res.json();
          if (resData?.sessionId) autoSaveSessionIdRef.current = resData.sessionId;
          logEvent("session_autosave", {
            card_type: selectedCardType,
            entries: draftEntries.length,
          });
        } else {
          dbg.warn("[autosave] response not ok", res.status);
        }
      } catch (e) {
        dbg.warn("[autosave] failed", e);
      }
    }, delay);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [draftEntries, sessionMode, clerkUser?.id]);

  useEffect(() => {
    const uid = clerkUser?.id;
    dbg.debug("fetchSessions(API) trigger", { uid });
  
    if (!uid) return;
  
    const fetchSessionsViaApi = async () => {
      try {
        const res = await fetch("/api/sessions", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list: any[] = Array.isArray(json.items) ? json.items : [];
  
        const sessions = list.map((data) => ({
          id: data.id,
          sessionId: data.sessionId ?? data.id,
          topic: data.topic ?? "",
          cardType: data.cardType ?? "",
          date: (data.updatedAt ?? data.createdAt ?? data.date ?? new Date().toISOString()),
          entries: Array.isArray(data.entries)
            ? data.entries.map((e: any) => ({ ...e, text: e.text ?? e.response ?? "" }))
            : [],
          answerToTopic: data.answerToTopic ?? "",
          interactionType: data.interactionType ?? "",
          isDraft: data.isDraft == null ? true : !!data.isDraft,
          uid,
        }));
  
        if (skipNextMirrorRef.current) {
          skipNextMirrorRef.current = false;
          log("[fetchSessions(API)] skip apply due to skipNextMirror");
        } else {
          setRecordedEntries(sessions);
          log("[fetchSessions(API)] applied", {
            count: sessions.length,
            ids: sessions.map((s) => s.sessionId ?? s.id),
          });
        }
      } catch (error) {
        console.error("セッション一覧の取得失敗(API)", error);
        setRecordedEntries([]);
      }
    };
  
    fetchSessionsViaApi();
  }, [clerkUser?.id]);

  // （任意）ユーザー切替時は再ハイドレートを許可
  useEffect(() => {
    hydratedCurrentRef.current = false;
  }, [clerkUser?.id]);

  useEffect(() => {
    setInputEntries([]); // ユーザー切替時は下書きを破棄
  }, [clerkUser?.id]);

  // ユーザーがいなくなったら保存キーも削除
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!clerkUser?.id) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }, [clerkUser?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (currentSessionId === null) {
        // 左ペインを空にしたい明示状態はセンチネルで固定保存
        localStorage.setItem(STORAGE_KEY, SKIP_SENTINEL); // 例: "__NONE__"
      } else if (currentSessionId) {
        // 通常は現在のセッションIDを保存
        localStorage.setItem(STORAGE_KEY, currentSessionId);
      }
      // undefined のときは何もしない
    } catch {}
  }, [currentSessionId]);

  // autoRestore（localStorage優先）: currentSessionId の決定 **だけ** を行う
  useEffect(() => {
    // すでに復元済みなら何もしない
    if (hydratedCurrentRef.current) return;
  
    // 保存直後の1回だけ、自動復元をスキップ（← ここで hydrated を必ず立てて確定させる）
    if (suppressAutoRestoreOnceRef.current) {
      suppressAutoRestoreOnceRef.current = false;
      dbg.debug("autoRestore: skipped once after save");
      hydratedCurrentRef.current = true;      // ← これが無いと後でまた走ってしまう
      return;
    }
  
    let storedId: string | null = null;
    if (typeof window !== 'undefined') {
      try { storedId = localStorage.getItem(STORAGE_KEY); } catch {}
    }
  
    // センチネルなら復元しない
    if (storedId === '__NONE__') {
      dbg.debug("autoRestore: sentinel __NONE__ -> do nothing");
      hydratedCurrentRef.current = true;
      return;
    }
  
    // 値が無ければ何もしない（recordedEntries から推測して立てない）
    if (!storedId) {
      dbg.debug("autoRestore: no storedId -> idle");
      hydratedCurrentRef.current = true;
      return;
    }
  
    // ここで初めて復元
    setCurrentSessionId(storedId);
    hydratedCurrentRef.current = true;
    dbg.debug("autoRestore: restored", { storedId });
  }, []); // ← 依存は空。recordedEntries を入れない

  useEffect(() => {
    const runId = ++hydrateRunId.current;
  
    if (!currentSessionId) {
      // ❗ ここでは右ペインを自動クリアしない（新セッションが選択/作成されるまで現状維持）
      log('[hydrate] idle (no currentSessionId); keep right pane as-is', { runId });
      return;
    }
  
    const picked = recordedEntries.find(
      (s) => (s.sessionId ?? s.id) === currentSessionId
    );
  
    if ((!picked || !Array.isArray(picked.entries)) && lastSavedRef.current?.id === currentSessionId) {
      log('[hydrate] skip: lastSaved but not found in recordedEntries yet', { currentSessionId, runId });
      return;
    }
    if (picked && Array.isArray(picked.entries) && picked.entries.length === 0 && lastSavedRef.current?.id === currentSessionId) {
      log('[hydrate] skip: lastSaved but entries empty', { currentSessionId, runId });
      return;
    }
  
    setRecentSession(picked ?? null);
    const shouldRestoreInputs = !!picked?.isDraft;
    const restoredEntries = shouldRestoreInputs ? (Array.isArray(picked?.entries) ? picked!.entries : []) : [];
    setInputEntries([]); // 旧参照のクリア
    setDraftEntries(restoredEntries);
    log('[hydrate] applied', { id: currentSessionId, found: !!picked, isDraft: !!picked?.isDraft, entriesLen: picked?.entries?.length ?? 0, runId });
  }, [currentSessionId, recordedEntries]); // ← recentSession 依存を外す！  

  const fixCurrentSessionId = (sessionId: string) => {
    suppressAutoRestoreOnceRef.current = true;
    hydratedCurrentRef.current = true;
    lastSavedRef.current = { id: sessionId, at: Date.now() }; // 🧷 直近保存IDを覚える
    setCurrentSessionId(sessionId);
    log('[fixCurrentSessionId]', sessionId);
  };
  
  /** 保存直後に呼ぶユーティリティ ... */
  const commitSavedSession = (sessionId: string, saved?: Partial<Session>) => {
    logEvent("session_save", {
      cardType: selectedCardType,
      interactionType: saved?.interactionType ?? "card",
      entriesLen: saved?.entries?.length ?? 0,
      topic: saved?.topic ?? null
    });

    log('[commitSavedSession:start]', { sessionId, savedEntries: saved?.entries ? (saved!.entries as any[]).length : null });
    fixCurrentSessionId(sessionId);
    lastSavedRef.current = { id: sessionId, at: Date.now() };
  
    if (saved) {
      setRecordedEntries(prev => {
        const merged: Session = {
          id: saved.id ?? saved.sessionId ?? sessionId,
          sessionId,
          topic: saved.topic ?? "",
          cardType: saved.cardType ?? selectedCardType ?? "",
          date: (saved as any)?.createdAt ?? (saved as any)?.date ?? new Date().toISOString(),
          entries: Array.isArray(saved.entries) ? (saved.entries as RecordEntry[]) : [],
          answerToTopic: saved.answerToTopic ?? "",
          interactionType: saved.interactionType ?? "",
          // 保存直後は必ず「下書き」扱いで左ペインに載せる
          isDraft: saved?.isDraft ?? true,
          uid: saved.uid ?? clerkUser?.id,
        };
        const next = [merged, ...prev.filter(s => (s.sessionId ?? s.id) !== sessionId)];
        log('[commitSavedSession:optimisticApplied]', { sessionId, entriesLen: merged.entries.length, listCount: next.length });
        return next;
      });
    } else {
      log('[commitSavedSession:noSavedPayload]', sessionId);
    }
  
    clearInputEntries();
    setDraftEntries([]);
    log('[commitSavedSession:done]', sessionId);
  };

  const optimisticArchive = (sessionId: string) => {
    log('[optimisticArchive]', sessionId);
    // 左ペインからなくす（isDraft=false）
    setRecordedEntries(prev =>
      prev.map(s =>
        ((s.sessionId ?? s.id) === sessionId) ? { ...s, isDraft: false } : s
      )
    );
    // 自動復元を次回だけ抑止（localStorage に __NONE__ を入れる）
    skipAutoRestoreNextLoad();
    // currentSessionId を null にしても右ペインは維持される設計（hydrate内のコメント通り）
    setCurrentSessionId(null);
  };  

  // =========================
  // UI専用：実効カード権限（override / facilitator を反映）
  // =========================
  const cardCaps = useMemo(() => {
    // 2026-08-29・案b実装：extraAllowedCardTypes（コース購入entitlement由来）をallowedへ
    // 追加でマージする（重複除去）。editions等の既存判定結果は変更せず、加算のみ。
    const mergeExtra = (base: { allowed: CardType[]; selected: CardType; can_switch: boolean }) => {
      if (extraAllowedCardTypes.length === 0) return base;
      const merged = Array.from(new Set([...base.allowed, ...extraAllowedCardTypes]));
      return { ...base, allowed: merged };
    };
    if (!userProfile) {
      return mergeExtra({
        allowed: ["free"] as CardType[],
        selected: "free" as CardType,
        can_switch: false,
      });
    }
    return mergeExtra(computeCardCapabilities(userProfile as any));
  }, [userProfile, extraAllowedCardTypes]);

  // REMINDER: useMemo/useEffect の依存配列に skipAutoRestoreNextLoad と suppressAutoRestoreOnceRef を入れ忘れないこと
  const value = useMemo(() => ({
    log: logEvent,
   // =========================
  // 派生: effective_plan（UI専用・最小ルール）
  // =========================
  effective_plan: userProfile
  ? (getEffectivePlan(userProfile as any) as string)
  : null,

  // =========================
    // 🔑 UIは必ずこの値を見る（Firestore生値禁止）
    // =========================
    card_types_allowed: cardCaps.allowed,
    can_switch_card_type: cardCaps.can_switch,
    selectedCardType,
    extraAllowedCardTypes,
    setExtraAllowedCardTypes,
    inputEntries,
    setInputEntries,
    recordedEntries,
    setRecordedEntries,
    selectedHowToStep,
    setSelectedHowToStep,
    selectedTopic,
    setSelectedTopic,
    recordSidebarOpen,
    setRecordSidebarOpen,
    activeSidebarSection,
    setActiveSidebarSection,
    activeTopicsTab,
    setActiveTopicsTab,
    newTopic,
    setNewTopic,
    recordMode,
    setRecordMode,
    sidebarCollapsed,
    setSidebarCollapsed,
    setSelectedCardType: handleSetSelectedCardType,
    setSelectedCardTypeLocal: setSelectedCardType,
    selectedCards,
    setSelectedCards,
    openSection,
    setOpenSection,
    draftEntries,
    setDraftEntries,
    currentSessionId,
    setCurrentSessionId,
    fixCurrentSessionId,
    userProfile,
    setUserProfile,
    userProfileLoading,
    setUserProfileLoading,
    answerToTopic,
    setAnswerToTopic,
    recentSession,
    setRecentSession,
    cards,
    cardCaps,
    setCards,
    fetchUserProfile,
    userCourses,
    userCoursesStatus,
    refreshUserCourses,
    commitSavedSession,
    clearInputEntries,
    suppressAutoRestoreOnceRef,
    skipAutoRestoreNextLoad,
    skipNextMirror,
    optimisticArchive,
    planRevision,
    sessionMode,
    setSessionMode,
  }), [
    // ここは「状態値」だけに絞る（setXxxは一切含めない！）
    inputEntries,
    recordedEntries,
    selectedHowToStep,
    selectedTopic,
    recordSidebarOpen,
    activeSidebarSection,
    activeTopicsTab,
    newTopic,
    recordMode,
    sidebarCollapsed,
    selectedCardType,
    extraAllowedCardTypes,
    selectedCards,
    openSection,
    draftEntries,
    currentSessionId,
    userProfile,
    userProfileLoading,
    userCourses,
    userCoursesStatus,
    refreshUserCourses,
    answerToTopic,
    recentSession,
    cards,
    cardCaps,
    planRevision,
    sessionMode,
  ]);

  return (
    <RecordContext.Provider value={value}>
      {children}
    </RecordContext.Provider>
  );
};

export const useRecordContext = () => {
  const context = useContext(RecordContext);
  if (!context) {
    // Provider の外から呼ばれたら即エラーにして、Hook 順序を壊さない
    throw new Error("useRecordContext must be used within RecordProvider");
  }
  return context;
};
